import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import AuthShell from "@/components/AuthShell";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useHashToken } from "@/hooks/useHashToken";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";

const RESET_PASSWORD = graphql(`
  mutation ResetPassword($token: String!, $password: String!, $passwordConfirmation: String!) {
    resetPassword(input: { token: $token, password: $password, passwordConfirmation: $passwordConfirmation }) {
      success
      errors
    }
  }
`);

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useHashToken();
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setLoading(true);
    try {
      const { resetPassword } = await graphqlClient.request(RESET_PASSWORD, {
        token,
        password,
        passwordConfirmation,
      });
      if (resetPassword.success) {
        toast({ title: t("auth.resetPassword.toast.successTitle"), description: t("auth.resetPassword.toast.successMessage") });
        navigate("/signin");
      } else {
        toast({ title: t("auth.resetPassword.toast.errorTitle"), description: resetPassword.errors[0], variant: "destructive" });
      }
    } catch (err) {
      console.error("ResetPassword error:", err);
      toast({ title: t("auth.resetPassword.toast.errorTitle"), description: err instanceof Error ? err.message : t("auth.resetPassword.toast.genericError"), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // A mail app that dropped the part after `#`: the form's button would do
  // nothing, so ask for a new link instead.
  if (!token) return <Navigate to="/forgot-password" replace />;

  return (
    <AuthShell>
        <Card className="border-border/50">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t("auth.resetPassword.title")}</CardTitle>
            <CardDescription>{t("auth.resetPassword.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t("auth.resetPassword.passwordLabel")}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="passwordConfirmation">{t("auth.resetPassword.confirmPasswordLabel")}</Label>
                <Input
                  id="passwordConfirmation"
                  type="password"
                  placeholder="••••••••"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t("auth.resetPassword.submitting") : t("auth.resetPassword.submit")}
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              <Link to="/signin" className="text-foreground hover:underline font-medium">{t("auth.resetPassword.backToSignIn")}</Link>
            </p>
          </CardContent>
        </Card>
    </AuthShell>
  );
};

export default ResetPassword;
